// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#audit-log
// Hook owning all data-fetching and filter state for the audit log table:
// three queries (apps, users, audit events), URL-synced filter state, and
// cursor-stack pagination.
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '@/lib/api';
import { AuditEventSchema } from '@greenlight/contract';
import type { z } from 'zod';

type AuditEvent = z.infer<typeof AuditEventSchema>;

/** Shared query-key prefix for the audit list. */
export const AUDIT_LIST_KEY = 'audit-list';

const PAGE_SIZE = 40;

export type TimeRange = 'all' | '1h' | '24h' | '7d';

const RANGE_MS: Record<Exclude<TimeRange, 'all'>, number> = {
  '1h': 3_600_000,
  '24h': 86_400_000,
  '7d': 604_800_000,
};

export const RANGE_LABEL: Record<TimeRange, string> = {
  all: 'All time',
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
};

const RANGE_REFETCH_MS: Record<Exclude<TimeRange, 'all'>, number> = {
  '1h': 60_000,
  '24h': 300_000,
  '7d': 3_600_000,
};

const RANGE_BUCKET_MS: Record<Exclude<TimeRange, 'all'>, number> = {
  '1h': 60_000,
  '24h': 300_000,
  '7d': 3_600_000,
};

function parseRange(raw: string | null): TimeRange {
  return raw === '1h' || raw === '24h' || raw === '7d' ? raw : 'all';
}

// Relative ranges bucket `from` so the query key and refetch interval stay in
// sync as the window slides. Filtering itself runs server-side on created_at.
function rangeQueryState(range: TimeRange): { from?: string; bucket: string | null } {
  if (range === 'all') return { bucket: null };
  const bucketMs = RANGE_BUCKET_MS[range];
  const bucketStart = Math.floor(Date.now() / bucketMs) * bucketMs;
  return {
    from: new Date(bucketStart - RANGE_MS[range]).toISOString(),
    bucket: String(bucketStart),
  };
}

function parseCursorStack(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split('|').map((c) => (c === '_' ? '' : decodeURIComponent(c)));
}

function serializeCursorStack(stack: string[]): string {
  return stack.map((c) => (c ? encodeURIComponent(c) : '_')).join('|');
}

export interface UseAuditLogResult {
  // Query state
  items: AuditEvent[];
  isLoading: boolean;
  isError: boolean;
  nextCursor: string | null;
  // Filter values (URL-driven)
  action: string;
  appId: string;
  actorUserId: string;
  range: TimeRange;
  cursor: string | undefined;
  cursorStack: string[];
  hasFilters: boolean;
  // Lookup helpers
  apps: { app_id: string; slug: string }[];
  actorName: (id: string) => string;
  appName: (id: string) => string;
  userMap: Map<string, { display_name: string | null; email: string }>;
  // Filter setters
  setFilter: (key: string, value: string) => void;
  // Pagination
  goNext: () => void;
  goPrev: () => void;
  clearFilters: () => void;
}

export function useAuditLog(): UseAuditLogResult {
  // Filter + paging state lives in the URL so an investigation is deep-linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get('q') ?? '';
  const appId = searchParams.get('app') ?? '';
  const actorUserId = searchParams.get('actor') ?? '';
  const range = parseRange(searchParams.get('range'));
  const cursor = searchParams.get('cursor') ?? undefined;
  const cursorStack = parseCursorStack(searchParams.get('pstack'));

  const updateParams = React.useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Setting a filter always drops back to the first page.
  const setFilter = React.useCallback(
    (key: string, value: string) => {
      updateParams((p) => {
        if (value) p.set(key, value);
        else p.delete(key);
        p.delete('cursor');
        p.delete('pstack');
      });
    },
    [updateParams],
  );

  // App + user lists resolve the audit row's ids (the contract carries ids only)
  // to the slug and person name shown in the table, and populate the App filter.
  const appsQuery = api.apps.list.useQuery(['audit-apps'], { query: { limit: 100 } });
  const apps = appsQuery.data?.status === 200 ? appsQuery.data.body.items : [];
  const appSlug = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const a of apps) m.set(a.app_id, a.slug);
    return m;
  }, [apps]);

  const usersQuery = api.users.list.useQuery(['audit-users'], { query: { limit: 100 } });
  const users = usersQuery.data?.status === 200 ? usersQuery.data.body.items : [];
  const userMap = React.useMemo(() => {
    const m = new Map<string, { display_name: string | null; email: string }>();
    for (const u of users) m.set(u.id, { display_name: u.display_name, email: u.email });
    return m;
  }, [users]);

  const actorName = React.useCallback(
    (id: string): string => {
      const u = userMap.get(id);
      return u?.display_name ?? u?.email ?? 'Unknown user';
    },
    [userMap],
  );
  const appName = React.useCallback(
    (id: string): string => appSlug.get(id) ?? id.slice(0, 8),
    [appSlug],
  );

  const { from, bucket } = rangeQueryState(range);
  const query = {
    limit: PAGE_SIZE,
    ...(action ? { action } : {}),
    ...(appId ? { appId } : {}),
    ...(actorUserId ? { actorUserId } : {}),
    ...(from ? { from } : {}),
    ...(cursor ? { cursor } : {}),
  };
  const key = [AUDIT_LIST_KEY, action, appId, actorUserId, range, bucket, cursor ?? null];

  const { data, isLoading, isError } = api.audit.list.useQuery(
    key,
    { query },
    {
      queryKey: key,
      refetchInterval: range !== 'all' ? RANGE_REFETCH_MS[range] : false,
    },
  );

  const items = data?.status === 200 ? data.body.items : [];
  const nextCursor = data?.status === 200 ? data.body.next_cursor : null;
  const hasFilters = action !== '' || appId !== '' || actorUserId !== '' || range !== 'all';

  function goNext(): void {
    if (!nextCursor) return;
    const nextStack = [...cursorStack, cursor ?? ''];
    updateParams((p) => {
      p.set('cursor', nextCursor);
      p.set('pstack', serializeCursorStack(nextStack));
    });
  }

  function goPrev(): void {
    if (cursorStack.length > 0) {
      const prev = cursorStack[cursorStack.length - 1]!;
      const nextStack = cursorStack.slice(0, -1);
      updateParams((p) => {
        if (prev) p.set('cursor', prev);
        else p.delete('cursor');
        if (nextStack.length) p.set('pstack', serializeCursorStack(nextStack));
        else p.delete('pstack');
      });
      return;
    }
    if (!cursor) return;
    updateParams((p) => {
      p.delete('cursor');
      p.delete('pstack');
    });
  }

  function clearFilters(): void {
    updateParams((p) => {
      for (const k of ['q', 'app', 'actor', 'range', 'cursor', 'pstack']) p.delete(k);
    });
  }

  return {
    items,
    isLoading,
    isError,
    nextCursor,
    action,
    appId,
    actorUserId,
    range,
    cursor,
    cursorStack,
    hasFilters,
    apps,
    actorName,
    appName,
    userMap,
    setFilter,
    goNext,
    goPrev,
    clearFilters,
  };
}
